import { useState, useEffect } from "react";
import http from "../../http";
import { Maquina } from "../../../interfaces/maquina.interface";
import { calcularGastoDepreciacao, calcularGastoEnergia, calcularGastoMaoDeObra } from "../../../utils/calculos";
import { Colaborador } from "../../../interfaces/colaborador.interface";
import Botao from "../../Botao";
import styles from './FormGastos.module.scss'

interface FormGastosProdutivosProps {
    quantidadeEntrada: number;
    onGetGastos: (depreciacao: number, energia: number, maoDeObra: number, quantidadeFinal: number, insumos: number) => void;
}

export default function FormGastosProdutivos({ quantidadeEntrada, onGetGastos }: FormGastosProdutivosProps) {

    const [maquinas, setMaquinas] = useState<Maquina[]>([]);
    const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);

    const [maquina, setMaquina] = useState<Maquina | undefined>(undefined);
    const [colaborador, setColaborador] = useState<Colaborador | undefined>(undefined);
    const [horasTrabalhadas, setHorasTrabalhadas] = useState<number>(0);
    const [insumos, setInsumos] = useState<number>(0);

    const [quantidadeFinal, setQuantidadeFinal] = useState<string>("");
    const [perda, setPerda] = useState<number>(0);
    const [rendimento, setRendimento] = useState<number>(0);

    // Função para buscar dados das máquinas e colaboradores
    const buscaDados = async () => {
        try {
            const dadosMaquinas = await http.get('maquinas');
            const dadosColaboradores = await http.get('colaboradores');
            setMaquinas(dadosMaquinas.data);
            setColaboradores(dadosColaboradores.data);
        } catch (error) {
            console.error("Erro ao buscar dados:", error);
        }
    };

    const calcularGastos = (e: SubmitEvent) => {
        e.preventDefault();

        if (maquina !== undefined && colaborador !== undefined) {
            const depreciacao = calcularGastoDepreciacao(maquina.valor, maquina.vidaUtil, horasTrabalhadas);
            const energia = maquina.fonteEnergia === 'diesel' ? calcularGastoEnergia(horasTrabalhadas) : 0;
            const maoDeObra = calcularGastoMaoDeObra(horasTrabalhadas, colaborador.valorHora);
            onGetGastos(Number(depreciacao), Number(energia), Number(maoDeObra), Number(quantidadeFinal), insumos);
        } else {
            console.error("Máquina ou colaborador não definido.");
        }
    };

    useEffect(() => {
        buscaDados();
    }, []);

    // Atualização automática do cálculo de perda e rendimento
    useEffect(() => {
        if (quantidadeEntrada && quantidadeFinal) {
            const perdaCalculada = ((quantidadeEntrada - Number(quantidadeFinal)) / quantidadeEntrada) * 100;
            setPerda(perdaCalculada);
            setRendimento(100 - perdaCalculada);
        }
    }, [quantidadeEntrada, quantidadeFinal]);

    return (
        <div className={styles.container}>
            <form className={styles.formulario}>
                <fieldset className={styles.fieldset}>
                    <legend className={styles.legend}>Materia-prima</legend>
                    <label className={styles.label}>Quantidade de entrada</label>
                    <input
                        className={styles.inputCampo}
                        value={quantidadeEntrada || 0}
                        type="number"
                        readOnly />
                </fieldset>

                <fieldset className={styles.fieldset}>
                    <legend className={styles.legend}>Dados do processo</legend>
                    <label className={styles.label}>Escolha a maquina</label>
                    <select
                        className={styles.selectCampo}
                        value={maquina?.id || ""}
                        onChange={(e) => {
                            const selectedMaquina = maquinas.find(m => m.id === e.target.value);
                            setMaquina(selectedMaquina || undefined);
                        }}
                        required
                    >
                        <option value="">Selecione</option>
                        {maquinas.map((maquina) => (
                            <option key={maquina.id} value={maquina.id}>
                                {maquina.nome}
                            </option>
                        ))}
                    </select>

                    <label className={styles.label}>Selecione o colaborador</label>
                    <select
                        className={styles.selectCampo}
                        value={colaborador?.id || ''}
                        onChange={(e) => {
                            const selectedColaborador = colaboradores.find(c => c.id === e.target.value);
                            setColaborador(selectedColaborador);
                        }}
                        required
                    >
                        <option value="">Selecione</option>
                        {colaboradores.map((colaborador) => (
                            <option key={colaborador.id} value={colaborador.id || undefined}>
                                {colaborador.nome}
                            </option>
                        ))}
                    </select>

                    <label className={styles.label}>Horas trabalhadas</label>
                    <input
                        className={styles.inputCampo}
                        value={horasTrabalhadas}
                        onChange={(e) => setHorasTrabalhadas(Number(e.target.value))}
                        type="number"
                        required />

                    <label className={styles.label}>Gastos com insumos</label>
                    <input
                        className={styles.inputCampo}
                        value={insumos}
                        onChange={(e) => setInsumos(Number(e.target.value))}
                        type="number"
                        required />
                </fieldset>

                <fieldset className={`${styles.fieldset} ${styles.resultados}`}>
                    <legend className={styles.legend}>Resultado</legend>
                    <label className={styles.label}>Quantidade Final</label>
                    <input
                        className={styles.inputCampo}
                        value={quantidadeFinal}
                        onChange={(e) => setQuantidadeFinal(e.target.value)}
                        type="number" />
                    <label className={styles.label}>Perda</label>
                    <input className={styles.inputCampo} value={`${perda.toFixed(2)}%`} readOnly />
                    <label className={styles.label}>Rendimento</label>
                    <input className={styles.inputCampo} value={`${rendimento.toFixed(2)}%`} readOnly />
                </fieldset>
            </form>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Botao text={'calcular'} onClick={calcularGastos} />
            </div>
        </div>
    );
}
